// Signals that a lead is becoming active (docs/specs/SIGNALS.md; proposed steps 2.35, 2.36, 10.16, 10.17): the words,
// types and small rules the screens share. The roles a play could replace are read with the play (crm_plays) and
// written through act set_play_signals; firms hiring for one of those roles are read with crm_hiring_signals (Find's
// weekly card) or from the firm's own signals (the firm page); contacts who may have moved job are read with
// crm_person_moves and decided through act decide_move (ReConnect › Spoken to before).
import type { Json } from "./types";

export interface PlayRole {
  title: string;
  keywords: string[];
}

export interface PlaySignals {
  roles: PlayRole[];
  exclude: string[];
  drafted_by: "claude" | "rules" | "owner" | null;
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

/** A play's signals block as the server returns it, with every field present. */
export function readPlaySignals(v: unknown): PlaySignals {
  const s = isObj(v) ? v : {};
  const roles = (Array.isArray(s.roles) ? s.roles : [])
    .filter(isObj)
    .map((r) => ({ title: String(r.title ?? "").trim(), keywords: (Array.isArray(r.keywords) ? r.keywords : []).map((k) => String(k).trim()).filter(Boolean) }))
    .filter((r) => r.title);
  const exclude = (Array.isArray(s.exclude) ? s.exclude : []).map((x) => String(x).trim()).filter(Boolean);
  const by = s.drafted_by === "claude" || s.drafted_by === "rules" || s.drafted_by === "owner" ? s.drafted_by : null;
  return { roles, exclude, drafted_by: by };
}

export const DRAFTED_WORDS: Record<NonNullable<PlaySignals["drafted_by"]>, string> = {
  claude: "Drafted by Ogma from your offer: check and edit them",
  rules: "Made from your offer's words: check and edit them",
  owner: "Your list",
};

/** "Water hygiene engineer, Legionella risk assessor" from the editor's lines; blank lines dropped, repeats once. */
export function parseList(text: string, max = 30): string[] {
  const out: string[] = [];
  for (const line of text.split(/[\n,]/)) {
    const v = line.replace(/\s+/g, " ").trim();
    if (v.length >= 2 && !out.some((x) => x.toLowerCase() === v.toLowerCase())) out.push(v.slice(0, 80));
    if (out.length >= max) break;
  }
  return out;
}

/** The roles editor's text back into roles, keeping each title's keywords when the title is unchanged. */
export function rolesFromText(text: string, before: PlayRole[]): PlayRole[] {
  return parseList(text).map((title) => ({ title, keywords: before.find((r) => r.title.toLowerCase() === title.toLowerCase())?.keywords ?? [] }));
}

/** The angle a play could add once it has roles: offer the business instead of a hire. */
export const INSTEAD_OF_HIRING_ANGLE = {
  key: "instead_of_hiring",
  label: "Instead of hiring",
  line: "You're advertising for this role: we could do the work for you instead, without the cost of a new hire.",
};

// ---------------------------------------------------------------------------
// 2.35 Hiring for a role you could fill
// ---------------------------------------------------------------------------
export interface HiringRow {
  signal_id: string;
  organisation_id: string | null;
  organisation: string | null;
  advert: string | null;
  role: string | null;
  url: string | null;
  location: string | null;
  observed_at: string;
}

interface SignalLike {
  kind: string;
  title?: string | null;
  url?: string | null;
  observed_at?: string;
  detail?: Json | null;
}

/** The firm page's line: the newest advert for a role the business could fill, or null. */
export function hiringLine(signals: SignalLike[]): { text: string; url: string | null } | null {
  const hit = signals.find((s) => s.kind === "job_post" && isObj(s.detail) && s.detail.subtype === "role_match");
  if (!hit || !isObj(hit.detail)) return null;
  const advert = String(hit.detail.advert_title ?? hit.title ?? "").replace(/^Hiring:\s*/, "");
  const role = hit.detail.role ? String(hit.detail.role) : null;
  const text = `Hiring for a role you could fill: ${advert}` + (role && role.toLowerCase() !== advert.toLowerCase() ? ` (your role: ${role})` : "");
  return { text, url: hit.url ?? (typeof hit.detail.url === "string" ? hit.detail.url : null) };
}

/** A team change's words for the firm page (2.36). */
export const TEAM_WORDS: Record<string, string> = {
  new_senior_hire: "New senior hire",
  director_appointed: "New director",
  departure: "Someone senior left",
};

// ---------------------------------------------------------------------------
// 10.16, 10.17 Contacts who moved job
// ---------------------------------------------------------------------------
export type Strength = "strong" | "medium" | "weak";

export interface MoveEvidence {
  source: string;
  strength: Strength;
  url?: string;
  ref?: string;
  note?: string;
}

export interface MoveRow {
  id: string;
  person_id: string;
  full_name: string | null;
  status: "suggested" | "confirmed" | "dismissed";
  from_org: string | null;
  from_name: string | null;
  to_org: string | null;
  to_name: string | null;
  to_domain: string | null;
  to_role: string | null;
  has_new_email: boolean;
  /** The new address the evidence gave, and where it came from (shown before the owner chooses to use it). */
  to_email?: string | null;
  to_email_source?: string | null;
  evidence: MoveEvidence[];
  confidence: number;
  created_at: string;
  decided_at: string | null;
  warmth: string | null;
  follow_up: { status: string; at: string; step: string } | null;
}

export const SOURCE_WORDS: Record<string, string> = {
  companies_house: "Companies House",
  website: "their new firm's website",
  auto_reply: "their automatic reply",
  register: "a public register",
};

export const STRENGTH_WORDS: Record<Strength, string> = { strong: "Strong", medium: "Good", weak: "Weak" };

/** "Fairly sure" etc. for a confidence from 0 to 1. */
export function confidenceWord(c: number): string {
  if (c >= 0.85) return "Very likely";
  if (c >= 0.7) return "Likely";
  return "Possible";
}

/** Where they went, in words: the firm's name, else its web address. */
export function moveTarget(m: Pick<MoveRow, "to_name" | "to_domain">): string {
  return m.to_name || m.to_domain || "a new firm";
}

export type MoveDecision = "confirm" | "dismiss" | "follow_up";

export interface MoveResult {
  status: string;
  routed?: string;
  organisation_id?: string;
  new_email?: boolean;
  do_not_contact?: boolean;
}

const ROUTED: Record<string, string> = {
  reconnect: "Ogma is drafting a follow-up for you to approve",
  complete: "their new firm is already a customer, so a follow-up task is in your actions",
  connect: "their new firm is already in a conversation with you, so a follow-up task is in your actions",
  network: "their new firm is held by Network, so a follow-up task is in your actions",
};

/** What the owner sees after a decision. */
export function moveSummary(name: string, r: MoveResult): string {
  if (r.status === "dismissed") return `Dismissed. Ogma won't suggest that firm for ${name} again.`;
  if (r.status === "confirmed")
    return `${name}'s new firm is linked. Their history with their old firm is kept.` +
      (r.new_email ? " Their new email address is saved." : "") +
      (r.do_not_contact ? " They asked not to be contacted, so that goes with them." : "");
  if (r.status === "already_planned") return `A follow-up for ${name} is already planned.`;
  if (r.status === "follow_up") return `${name}'s new firm is linked: ${ROUTED[r.routed ?? ""] ?? "a follow-up is planned"}.`;
  return "Done.";
}
